import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { LoadMoreFooter } from '@/components/LoadMoreFooter';
import { useProductsLoadMore, useProductsStats } from '@/features/product/hooks';
import {
  sortForList,
  useProductListControls,
  validateProductListSearch,
} from '@/features/product/list-controls';
import {
  ListEmptyState,
  ListErrorState,
  ListLoadingState,
} from '@/features/product/components/table-kit';
import { CreateProductMenu } from '@/features/product/components/CreateProductMenu';
import { ProductAccountTable } from '@/features/product/components/ProductAccountTable';
import { PageHero } from '@/components/layout/PageHero';

/**
 * Product Account — list of every submitted product the user has registered.
 *
 * Edit opens the product page (section-by-section spec); Archive / Restore act on the
 * row. Multi-jurisdiction backend split lands separately and the table's `expandToRows()`
 * flips to flatMap without column changes.
 */
export const Route = createFileRoute('/product-account')({
  validateSearch: validateProductListSearch,
  component: ProductAccountPage,
});

function ProductAccountPage() {
  // Keyset-paginated list (10/page, PER-PAGE Load more) + server-accurate
  // tile counts — the tiles must not be computed from loaded rows only.
  // Search + sort are SERVER params (debounced q; sort key + direction) so
  // results cover the whole roster, not just the loaded rows.
  const searchParams = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const {
    searchInput,
    setSearchInput,
    q,
    sort,
    onSort,
    productType,
    onProductType,
    filtering,
  } = useProductListControls(searchParams, navigate);
  const [showArchived, setShowArchived] = useState(false);
  const listSort = sortForList(sort, showArchived);
  const { visible, hasMore, isFetchingMore, loadMore, isLoading, isError, totalCount } =
    useProductsLoadMore(10, { q, sort: listSort, productType, archived: showArchived });
  const statsQuery = useProductsStats();
  const stats = {
    total: statsQuery.data?.total ?? 0,
    byDevice: statsQuery.data?.by_type['device'] ?? 0,
    byDrug: statsQuery.data?.by_type['drug'] ?? 0,
    byBiologic: statsQuery.data?.by_type['biologic'] ?? 0,
  };

  return (
    <>
      <PageHero
        title="Product Account"
        subtitle="Every product you've registered for regulatory work. Country is picked per agent run, so the same product can target any market without re-registering."
      />

      <main className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 pt-8 pb-20">
        {isLoading && <ListLoadingState />}
        {isError && <ListErrorState />}
        {!isLoading && !isError && visible.length === 0 && !filtering && !showArchived && (
          <ListEmptyState
            title="No products registered yet."
            body="Register your first product to start running regulatory strategy, predicate analysis, clinical protocols, and submission dossiers against it."
          >
            <div className="inline-flex">
              <CreateProductMenu />
            </div>
          </ListEmptyState>
        )}
        {!isLoading && !isError && (visible.length > 0 || filtering || showArchived) && (
          <>
            <ProductAccountTable
              products={visible}
              stats={stats}
              search={searchInput}
              onSearch={setSearchInput}
              sort={listSort}
              onSort={onSort}
              archived={showArchived}
              onArchivedChange={setShowArchived}
              productType={productType}
              onProductType={onProductType}
            />
            <LoadMoreFooter
              loaded={visible.length}
              total={totalCount}
              hasMore={hasMore}
              isFetching={isFetchingMore}
              onLoadMore={loadMore}
            />
          </>
        )}
      </main>
    </>
  );
}


